'use client'

// Third-party
import { useState } from 'react'
import { motion } from 'framer-motion'
import { User, Mail, Users, Phone, CheckCircle, AlertCircle } from 'lucide-react'

// First-party
import { sendRegistrationEmail as sendEmail } from '../utils/emailService'
import { RegistrationFormData as FormData } from '../interfaces/RegistrationFormData'

const categories = [
  { id: 'und-6', name: 'Under 6 (- 6 años)', description: 'Categoría under 06' },
  { id: 'und-8', name: 'Under 8 (- 8 años)', description: 'Categoría under 08' },
  { id: 'und-10', name: 'Under 10 (- 10 años)', description: 'Categoría under 10' },
  { id: 'und-12', name: 'Under 12 (- 12 años)', description: 'Categoría under 12' },
  { id: 'under-14', name: 'Under 14 (- 14 años)', description: 'Categoría under 14' },
  { id: 'under-16', name: 'Under-16 (- 16 años)', description: 'Categoría under 16' },
  { id: 'libre', name: 'Libre', description: 'categoría libre' },
  { id: 'master', name: 'Master (35+ años)', description: 'Categoría de veteranos' }
]


export default function RegistrationForm() {
  const [formData, setFormData] = useState<FormData>({
    nombre: '',
    email: '',
    nombreEquipo: '',
    categoriasInteresadas: [],
    numeroTelefonico: ''
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.nombre.trim()) {
      newErrors.nombre = 'El nombre es requerido'
    }

    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'El email no es válido'
    }

    if (!formData.nombreEquipo.trim()) {
      newErrors.nombreEquipo = 'El nombre del equipo es requerido'
    }

    if (formData.categoriasInteresadas.length === 0) {
      newErrors.categoriasInteresadas = 'Debe seleccionar al menos una categoría'
    }

    if (!formData.numeroTelefonico.trim()) {
      newErrors.numeroTelefonico = 'El número telefónico es requerido'
    } else if (!/^\+?[\d\s\-\(\)]{10,}$/.test(formData.numeroTelefonico)) {
      newErrors.numeroTelefonico = 'El número telefónico no es válido'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) return

    setIsSubmitting(true)

    // Simular envío del formulario
    setTimeout(() => {
      setIsSubmitting(false)
      setIsSubmitted(true)
    }, 2000)

    sendEmail({subject: 'New Liga Riviera Flag registration', title: 'New Registration', formData });
  }

  const handleCategoryChange = (categoryId: string) => {
    setFormData(prev => ({
      ...prev,
      categoriasInteresadas: prev.categoriasInteresadas.includes(categoryId)
        ? prev.categoriasInteresadas.filter(id => id !== categoryId)
        : [...prev.categoriasInteresadas, categoryId]
    }))
  }

  if (isSubmitted) {
    return (
      <section className="py-20 bg-gradient-to-b from-black via-gray-900 to-black">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-2xl mx-auto text-center"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              className="w-24 h-24 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-8"
              animate={{
                scale: [1, 1.1, 1],
                rotate: [0, 360]
              }}
              transition={{
                duration: 2,
                ease: "easeInOut"
              }}
            >
              <CheckCircle className="w-12 h-12 text-white" />
            </motion.div>
            
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
              ¡REGISTRO <span className="text-yellow-400">EXITOSO</span>!
            </h2>
            
            <p className="text-xl text-gray-300 mb-8">
              Hemos recibido tu pre-registro. Te contactaremos pronto con más información sobre la Liga Elite.
            </p>
            
            <motion.button
              onClick={() => {
                setIsSubmitted(false)
                setFormData({
                  nombre: '',
                  email: '',
                  nombreEquipo: '',
                  categoriasInteresadas: [],
                  numeroTelefonico: ''
                })
              }}
              className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black px-8 py-3 rounded-full font-bold text-lg hover:shadow-2xl transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              REGISTRAR OTRO EQUIPO
            </motion.button>
          </motion.div>
        </div>
      </section>
    )
  }

  return (
    <section className="py-20 bg-gradient-to-b from-black via-gray-900 to-black relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <motion.div
          className="absolute inset-0 opacity-20"
          animate={{
            background: [
              'radial-gradient(circle at 30% 40%, rgba(255, 196, 0, 0.1) 0%, transparent 70%)',
              'radial-gradient(circle at 70% 60%, rgba(255, 196, 0, 0.1) 0%, transparent 70%)',
              'radial-gradient(circle at 30% 40%, rgba(255, 196, 0, 0.1) 0%, transparent 70%)'
            ]
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <motion.h2 
            className="text-5xl md:text-6xl font-black text-white mb-6"
            style={{ fontWeight: 900 }}
          >
            PRE-<span className="text-yellow-400">REGISTRO</span>
          </motion.h2>
          <div className="h-1 w-32 bg-gradient-to-r from-yellow-400 to-orange-500 mx-auto mb-6" />
          <motion.p 
            className="text-xl text-gray-300 max-w-3xl mx-auto"
            style={{ fontWeight: 400 }}
          >
            Asegura tu lugar en la Liga Elite. El registro es gratuito y sin compromiso.
          </motion.p>
        </motion.div>

        <div className="max-w-4xl mx-auto">
          <motion.form
            onSubmit={handleSubmit}
            className="bg-gradient-to-br from-gray-800/50 to-gray-900/80 rounded-2xl p-8 backdrop-blur-sm border border-gray-700/50"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="grid md:grid-cols-2 gap-8">
              {/* Nombre */}
              <motion.div
                className="space-y-2"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <label className="flex items-center text-white font-semibold mb-2">
                  <User className="w-5 h-5 text-yellow-400 mr-2" />
                  Nombre Completo
                </label>
                <input
                  type="text"
                  value={formData.nombre}
                  onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                  className="w-full bg-black/50 border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/20 transition-all duration-300"
                  placeholder="Tu nombre completo"
                />
                {errors.nombre && (
                  <motion.p 
                    className="text-red-400 text-sm flex items-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.nombre}
                  </motion.p>
                )}
              </motion.div>

              {/* Email */}
              <motion.div
                className="space-y-2"
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <label className="flex items-center text-white font-semibold mb-2">
                  <Mail className="w-5 h-5 text-yellow-400 mr-2" />
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full bg-black/50 border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/20 transition-all duration-300"
                  placeholder="tu@email.com"
                />
                {errors.email && (
                  <motion.p 
                    className="text-red-400 text-sm flex items-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.email}
                  </motion.p>
                )}
              </motion.div>

              {/* Nombre del Equipo */}
              <motion.div
                className="space-y-2"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.5 }}
              >
                <label className="flex items-center text-white font-semibold mb-2">
                  <Users className="w-5 h-5 text-yellow-400 mr-2" />
                  Nombre del Equipo
                </label>
                <input
                  type="text"
                  value={formData.nombreEquipo}
                  onChange={(e) => setFormData(prev => ({ ...prev, nombreEquipo: e.target.value }))}
                  className="w-full bg-black/50 border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/20 transition-all duration-300"
                  placeholder="Nombre de tu equipo"
                />
                {errors.nombreEquipo && (
                  <motion.p 
                    className="text-red-400 text-sm flex items-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.nombreEquipo}
                  </motion.p>
                )}
              </motion.div>

              {/* Número Telefónico */}
              <motion.div
                className="space-y-2"
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                <label className="flex items-center text-white font-semibold mb-2">
                  <Phone className="w-5 h-5 text-yellow-400 mr-2" />
                  Número Telefónico
                </label>
                <input
                  type="tel"
                  value={formData.numeroTelefonico}
                  onChange={(e) => setFormData(prev => ({ ...prev, numeroTelefonico: e.target.value }))}
                  className="w-full bg-black/50 border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/20 transition-all duration-300"
                  placeholder="+52 998 123 4567"
                />
                {errors.numeroTelefonico && (
                  <motion.p 
                    className="text-red-400 text-sm flex items-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.numeroTelefonico}
                  </motion.p>
                )}
              </motion.div>
            </div>

            {/* Categorías Interesadas */}
            <motion.div
              className="mt-8 space-y-4"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.7 }}
            >
              <label className="text-white font-semibold text-lg mb-4 block">
                Categorías Interesadas
              </label>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categories.map((category, index) => (
                  <motion.div
                    key={category.id}
                    className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 ${
                      formData.categoriasInteresadas.includes(category.id)
                        ? 'border-yellow-400 bg-yellow-400/10' 
                        : 'border-gray-600 bg-black/30 hover:border-gray-500'
                    }`}
                    onClick={() => handleCategoryChange(category.id)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                  >
                    <div className="flex items-start space-x-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                        formData.categoriasInteresadas.includes(category.id)
                          ? 'border-yellow-400 bg-yellow-400'
                          : 'border-gray-500'
                      }`}>
                        {formData.categoriasInteresadas.includes(category.id) && (
                          <CheckCircle className="w-3 h-3 text-black" />
                        )}
                      </div>
                      <div>
                        <h4 className="text-white font-semibold">{category.name}</h4>
                        <p className="text-gray-400 text-sm">{category.description}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
              
              {errors.categoriasInteresadas && (
                <motion.p 
                  className="text-red-400 text-sm flex items-center"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.categoriasInteresadas}
                </motion.p>
              )}
            </motion.div>

            {/* Submit Button */}
            <motion.div
              className="mt-8 text-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              <motion.button
                type="submit"
                disabled={isSubmitting}
                className={`bg-gradient-to-r from-yellow-400 to-orange-500 text-black px-12 py-4 rounded-full text-xl font-bold transition-all duration-300 ${
                  isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:shadow-2xl'
                }`}
                whileHover={!isSubmitting ? { 
                  scale: 1.05,
                  boxShadow: '0 20px 40px rgba(255, 196, 0, 0.4)'
                } : {}}
                whileTap={!isSubmitting ? { scale: 0.95 } : {}}
                style={{ fontWeight: 700 }}
              >
                {isSubmitting ? (
                  <span className="flex items-center">
                    <motion.div
                      className="w-6 h-6 border-2 border-black border-t-transparent rounded-full mr-3"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    />
                    PROCESANDO...
                  </span>
                ) : (
                  'COMPLETAR PRE-REGISTRO'
                )}
              </motion.button>
              
              <p className="text-gray-400 text-sm mt-4">
                * El registro es completamente gratuito y sin compromiso
              </p>
            </motion.div>
          </motion.form>
        </div>
      </div>
    </section>
  )
}